/**
 * The public booking API, as the funnel calls it (MASTER_PLAN M5.7 and M6.10,
 * API.md §2.3 and §2.4, ADR-018, ARCHITECTURE.md §8.2 and §13).
 *
 * M6.10 added the four insurance routes — the payer picker, starting a check, polling it
 * and the draft's price. They are here rather than in a second client because they are
 * the same visitor and the same `ha_draft` cookie as everything else on this transport:
 * a separate module would be a second place to forget `credentials: 'include'`.
 *
 * One typed function per route, over the shared `@platform/api-client` transport, so
 * every funnel request already carries `credentials: 'include'` — the `ha_draft` cookie
 * is HttpOnly and signed, and a request that forgets it is a request that belongs to
 * nobody (SECURITY.md §4.4, ADR-018).
 *
 * ## Why the bodies are typed here rather than by the generated document
 *
 * `packages/api-client/src/generated/openapi.d.ts` types every public **response**, but
 * Nest's Swagger plugin emits `requestBody?: never` for these controllers — they take
 * `@Body() body: unknown` and validate with zod, so there is no decorated DTO for the
 * generator to read. `BodyOf<…>` therefore resolves to `undefined` and `client.post`
 * would refuse a body it will happily send.
 *
 * Rather than cast at eleven call sites, the request shapes are named here from
 * `@platform/contracts` — the very schemas the server parses with, so a drift is a
 * compile error — and the calls go through `client.request`, which is the same code
 * path `client.post` uses. Responses keep the generated types. When the API grows
 * decorated request DTOs this file loses its `request(...)` calls and nothing else.
 *
 * ## The one call that is not on the transport
 *
 * `recordEvents` uses `fetch` directly, because it needs `keepalive: true` and
 * `RequestOptions` has no such field: the `abandoned` event is fired from `pagehide`,
 * the one moment a normal request is cancelled as the document goes away (FR-BOOK-14).
 * It is fire-and-forget by design — analytics must never be able to fail a booking — so
 * it resolves to nothing and swallows every error.
 */

import { type ApiClient, type ApiError } from '@platform/api-client';
import {
  DecisionMessageKeySchema,
  type BookingAppointment,
  type CreateDraftRequest,
  type DecisionMessageKey,
  type DraftConsent,
  type DraftContent,
  type DraftPatch,
  type DraftStep,
  type DraftSummary,
  type EligibilityPublicStatus,
  type FunnelEventName,
  type FunnelEventProperties,
  type LocalDate,
  type PriceQuote,
  type PublicDecisionRequest,
  type PublicDecisionResponse,
  type PublicPayer,
  type Reservation,
  type SlotTakenDetails,
  type StartEligibilityRequest,
  type StoredDecision,
} from '@platform/contracts';

import { apiClient } from './client';

/* ------------------------------------------------------------------ *
 * Request shapes
 * ------------------------------------------------------------------ */

/** `POST /public/drafts`. Nothing is required: the cookie is the identity. */
export type CreateDraftInput = CreateDraftRequest;

export interface AvailabilityDatesQuery {
  /** The visit type's stable `key`, never its uuid — a marketing link carries the key. */
  type: string;
  /** Two-letter state of residence (FR-BOOK-03). */
  state: string;
  from?: LocalDate;
  to?: LocalDate;
}

export interface AvailabilitySlotsQuery {
  type: string;
  state: string;
  /** Clinic-local `YYYY-MM-DD`. */
  date: LocalDate;
}

export interface ReserveInput {
  /** Accepts the visit type's `key` or its uuid (API.md §3.1). */
  appointmentTypeId: string;
  /** The slot's instant, with an offset. */
  startAt: string;
  state: string;
}

/**
 * The patient half of confirm, as an **override**.
 *
 * Every field is optional because the draft is the default: the funnel has been
 * autosaving these since the details step and sending them again would mean the browser
 * holding a second copy of what the draft exists to keep off it (FR-BOOK-09,
 * ARCHITECTURE.md §13). The funnel sends none of them; the shape exists so a future
 * caller that genuinely has a correction can make it without a cast.
 */
export interface ConfirmPatientOverride {
  firstName?: string;
  lastName?: string;
  dateOfBirth?: string;
  email?: string;
  phone?: string | null;
  sex?: 'female' | 'male' | 'other' | 'unknown';
  state?: string;
  timeZone?: string;
  preferredLanguage?: string;
}

export interface ConfirmInput {
  reservationId: string;
  /** Omit unless something on the draft has to be corrected. */
  patient?: ConfirmPatientOverride;
  /** Omit to use the consents the draft recorded. `marketing` is dropped server-side. */
  consents?: DraftConsent['kind'][];
}

/**
 * One funnel event (FR-BOOK-14, ADR-019).
 *
 * `name` and `properties` are both closed sets in `@platform/contracts`, so "no PHI in
 * an analytics payload" is a compile-time property here and a `400` on the server — not
 * a discipline every call site has to keep.
 *
 * `draftId` is deliberately absent: the server resolves the draft from the same cookie
 * it checks a submitted id against, so sending one can only be redundant or wrong.
 */
export interface FunnelEventInput {
  name: FunnelEventName;
  step?: DraftStep | null;
  variants?: string[];
  properties?: FunnelEventProperties;
  occurredAt?: string;
}

/**
 * `POST /public/eligibility` (M6.10, API.md §2.4, FR-INS-03).
 *
 * The contract's own shape, so a drift is a compile error rather than a `400` in front
 * of a patient. The draft is NOT named: the `ha_draft` cookie is the identity, exactly
 * as it is for every other route here.
 */
export type StartEligibilityInput = StartEligibilityRequest;

export interface PayerSearchInput {
  /** Free text. Under two characters the endpoint answers the visible list, not an error. */
  q: string;
  limit?: number;
}

/* ------------------------------------------------------------------ *
 * Response shapes
 * ------------------------------------------------------------------ */

export interface AvailabilityDates {
  data: { date: string; slotCount: number }[];
  clinicTimeZone: string;
}

export interface AvailabilitySlots {
  data: { startAt: string; endAt: string; localTime: string }[];
  clinicTimeZone: string;
}

export interface ConfirmedBooking {
  appointment: BookingAppointment;
  portalLink: string;
}

export interface ResumedDraft {
  draft: DraftSummary;
  content: DraftContent;
}

/** `202` from `POST /public/eligibility`: an id to poll and a budget to poll it for. */
export interface StartedEligibility {
  checkId: string;
  /** Usually `pending`; a still-valid cached check comes back `verified` at once. */
  status: EligibilityPublicStatus;
  /** Milliseconds the client should keep polling before giving up (FR-INS-04). */
  pollBudgetMs: number;
}

/**
 * `GET /public/eligibility/:checkId`.
 *
 * Four states and never a benefit: `hintKey` is an i18n key the app translates, `quote`
 * is what the deterministic engine decided. No member id, no date of birth, no parsed
 * 271 — nothing here is PHI (packages/contracts/src/insurance.ts).
 */
export interface EligibilityStatus {
  checkId: string;
  status: EligibilityPublicStatus;
  hintKey: string | null;
  quote: PriceQuote | null;
  /**
   * M15: the post-verification scheduling decision (`StoredDecision`), null while the
   * check is pending or when the tenant runs no ruleset. Optional because an API that
   * predates M15 does not send it, and absent must read exactly like `null`.
   */
  decision?: StoredDecision | null;
}

/**
 * `POST /public/eligibility/decision` (M15, ADR-032): the pre-phase answer.
 *
 * `verify` means "run the check, the tree needs its answer"; the other three are the
 * tree's own outcomes. `messageKey` is null only for `verify`.
 */
export type SchedulingDecision = PublicDecisionResponse['data'];

export interface BookingApi {
  createDraft(input?: CreateDraftInput, signal?: AbortSignal): Promise<DraftSummary>;
  /** `null`, not a `404`, when this browser has nothing saved. */
  currentDraft(signal?: AbortSignal): Promise<DraftSummary | null>;
  /** The only route that decrypts the draft. Call it after the patient asks to. */
  resumeDraft(signal?: AbortSignal): Promise<ResumedDraft>;
  patchDraft(patch: DraftPatch, signal?: AbortSignal): Promise<DraftSummary>;
  discardDraft(): Promise<void>;

  availabilityDates(
    query: AvailabilityDatesQuery,
    signal?: AbortSignal,
  ): Promise<AvailabilityDates>;
  availabilitySlots(
    query: AvailabilitySlotsQuery,
    signal?: AbortSignal,
  ): Promise<AvailabilitySlots>;

  reserve(input: ReserveInput, signal?: AbortSignal): Promise<Reservation>;
  extendReservation(id: string): Promise<Reservation>;
  releaseReservation(id: string): Promise<void>;

  /** `Idempotency-Key` is required here; a missing one is a `400` (API.md §1.8). */
  confirmBooking(input: ConfirmInput, idempotencyKey: string): Promise<ConfirmedBooking>;

  /** The clinic's curated insurer list (FR-INS-01). Never the clearinghouse. */
  searchPayers(query: PayerSearchInput, signal?: AbortSignal): Promise<PublicPayer[]>;
  /** Start a coverage check. Answers `202` before the payer has (FR-INS-04). */
  startEligibility(input: StartEligibilityInput, signal?: AbortSignal): Promise<StartedEligibility>;
  /** Poll one check. Scoped to the draft this cookie owns. */
  eligibilityStatus(checkId: string, signal?: AbortSignal): Promise<EligibilityStatus>;
  /** The price for this draft, however it was reached. `null` before there is one. */
  currentQuote(signal?: AbortSignal): Promise<PriceQuote | null>;
  /**
   * The pre-verification scheduling decision (M15). Also stores the declared plan type on
   * the draft server-side, so it is called after the draft has been flushed.
   */
  eligibilityDecision(
    input: PublicDecisionRequest,
    signal?: AbortSignal,
  ): Promise<SchedulingDecision>;

  /** Fire and forget. Never throws, never resolves to anything worth reading. */
  recordEvents(events: FunnelEventInput[]): void;
}

const PUBLIC = '/api/v1/public';

export function createBookingApi(client: ApiClient): BookingApi {
  /** `signal` is threaded only where a component can outlive the request. */
  const withSignal = (signal: AbortSignal | undefined): { signal?: AbortSignal } =>
    signal === undefined ? {} : { signal };

  return {
    createDraft: async (input = {}, signal) => {
      const body = await client.request<{ data: { draft: DraftSummary } }>(
        'post',
        `${PUBLIC}/drafts`,
        input,
        withSignal(signal),
      );
      return body.data.draft;
    },

    /*
     * `request` rather than `client.get`: M15 added `insuranceType` and `decision` to the
     * summary, and the generated document is regenerated from the API, not from the
     * contract - so the contract's type is named here, as for every other draft route.
     */
    currentDraft: async (signal) => {
      const body = await client.request<{ data: { draft: DraftSummary | null } }>(
        'get',
        `${PUBLIC}/drafts/current`,
        undefined,
        withSignal(signal),
      );
      return body.data.draft;
    },

    resumeDraft: async (signal) => {
      const body = await client.request<{ data: ResumedDraft }>(
        'post',
        `${PUBLIC}/drafts/current/resume`,
        {},
        withSignal(signal),
      );
      return body.data;
    },

    patchDraft: async (patch, signal) => {
      const body = await client.request<{ data: { draft: DraftSummary } }>(
        'patch',
        `${PUBLIC}/drafts/current`,
        patch,
        withSignal(signal),
      );
      return body.data.draft;
    },

    discardDraft: async () => {
      await client.delete('/api/v1/public/drafts/current');
    },

    availabilityDates: async (query, signal) =>
      client.get('/api/v1/public/availability/dates', {
        query: { type: query.type, state: query.state, from: query.from, to: query.to },
        ...withSignal(signal),
      }),

    availabilitySlots: async (query, signal) =>
      client.get('/api/v1/public/availability/slots', {
        query: { type: query.type, state: query.state, date: query.date },
        ...withSignal(signal),
      }),

    reserve: async (input, signal) => {
      const body = await client.request<{ data: { reservation: Reservation } }>(
        'post',
        `${PUBLIC}/reservations`,
        input,
        withSignal(signal),
      );
      return body.data.reservation;
    },

    extendReservation: async (id) => {
      const body = await client.request<{ data: { reservation: Reservation } }>(
        'post',
        `${PUBLIC}/reservations/${encodeURIComponent(id)}/extend`,
        {},
      );
      return body.data.reservation;
    },

    releaseReservation: async (id) => {
      await client.request<void>(
        'delete',
        `${PUBLIC}/reservations/${encodeURIComponent(id)}`,
        undefined,
      );
    },

    confirmBooking: async (input, idempotencyKey) => {
      const body = await client.request<{ data: ConfirmedBooking }>(
        'post',
        `${PUBLIC}/bookings/confirm`,
        input,
        { idempotencyKey },
      );
      return body.data;
    },

    searchPayers: async (query, signal) => {
      const body = await client.get('/api/v1/public/payers', {
        query: { q: query.q, limit: query.limit },
        ...withSignal(signal),
      });
      return body.data;
    },

    startEligibility: async (input, signal) => {
      const body = await client.request<{ data: StartedEligibility }>(
        'post',
        `${PUBLIC}/eligibility`,
        input,
        withSignal(signal),
      );
      return body.data;
    },

    /*
     * Not `client.get`: the generated document spells this path with a `{checkId}`
     * placeholder, and an interpolated string is not that literal key. `request` is the
     * same code path with the type named here instead (see the header).
     */
    eligibilityStatus: async (checkId, signal) => {
      const body = await client.request<{ data: EligibilityStatus }>(
        'get',
        `${PUBLIC}/eligibility/${encodeURIComponent(checkId)}`,
        undefined,
        withSignal(signal),
      );
      return body.data;
    },

    currentQuote: async (signal) => {
      const body = await client.get('/api/v1/public/quote', withSignal(signal));
      return body.data.quote;
    },

    eligibilityDecision: async (input, signal) => {
      const body = await client.request<PublicDecisionResponse>(
        'post',
        `${PUBLIC}/eligibility/decision`,
        input,
        withSignal(signal),
      );
      return body.data;
    },

    recordEvents: (events) => {
      if (events.length === 0) return;
      if (typeof fetch !== 'function') return;
      void fetch(`${client.baseUrl}${PUBLIC}/events`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ events }),
        /*
         * The whole reason this one call bypasses the transport: `abandoned` is fired
         * from `pagehide`, and without this the browser cancels it as the document goes.
         */
        keepalive: true,
      }).catch(() => {
        /* Analytics must never be able to fail a booking (rules §35.6). */
      });
    },
  };
}

/** The funnel's own instance, over the app's single client and query cache. */
export const bookingApi: BookingApi = createBookingApi(apiClient);

/* ------------------------------------------------------------------ *
 * Error helpers
 * ------------------------------------------------------------------ */

/** `409 SLOT_TAKEN` — someone else reserved or booked the time first (API.md §3.3). */
export function isSlotTaken(error: unknown): error is ApiError {
  return codeOf(error) === 'SLOT_TAKEN';
}

/** `410 RESERVATION_EXPIRED` — the ten-minute hold ran out; the slot must be re-picked. */
export function isReservationExpired(error: unknown): error is ApiError {
  return codeOf(error) === 'RESERVATION_EXPIRED';
}

/** `400 VALIDATION_FAILED` — the server names the fields in `details.issues`. */
export function isValidationFailed(error: unknown): error is ApiError {
  return codeOf(error) === 'VALIDATION_FAILED';
}

/**
 * `429 RATE_LIMITED` — too many coverage checks from this draft (API.md §1.9).
 *
 * Its own helper because the funnel's answer is not an error message but a wait: five
 * checks in ten minutes is generous for one booking, and a patient who hits it has
 * almost certainly been retrying a member id that will not verify.
 */
export function isRateLimited(error: unknown): error is ApiError {
  return codeOf(error) === 'RATE_LIMITED';
}

/**
 * `409 SCHEDULING_BLOCKED` (M15, ADR-032) — the tenant's scheduling rules decided
 * `contact_office` for this draft, and confirm refuses. The normal path never gets here:
 * `/insurance` stops the patient first. This is the server's own guard answering.
 */
export function isSchedulingBlocked(error: unknown): error is ApiError {
  return codeOf(error) === 'SCHEDULING_BLOCKED';
}

/**
 * The message key a `409 SCHEDULING_BLOCKED` named, or the generic one. Read through the
 * closed enum, so a key this bundle does not know becomes the generic sentence rather
 * than a blank panel.
 */
export function blockedMessageKeyOf(error: unknown): DecisionMessageKey {
  const details = (error as { details?: { messageKey?: unknown } } | undefined)?.details;
  const parsed = DecisionMessageKeySchema.safeParse(details?.messageKey);
  return parsed.success ? parsed.data : 'decision.contact_office.generic';
}

/**
 * The alternatives a `409 SLOT_TAKEN` offered, or an empty list.
 *
 * Read defensively: API.md §1.6 sketches `alternatives` as bare ISO strings and §3.3 as
 * `{ startAt, endAt }` pairs. Both are accepted so that a funnel does not lose its
 * recovery path to a shape difference it cannot fix.
 */
export function alternativesOf(error: unknown): { startAt: string; endAt?: string }[] {
  const details = (error as { details?: Partial<SlotTakenDetails> } | undefined)?.details;
  const raw = details?.alternatives;
  if (!Array.isArray(raw)) return [];

  const out: { startAt: string; endAt?: string }[] = [];
  for (const entry of raw) {
    if (typeof entry === 'string') out.push({ startAt: entry });
    else if (typeof entry === 'object' && entry !== null && typeof entry.startAt === 'string') {
      out.push(typeof entry.endAt === 'string' ? entry : { startAt: entry.startAt });
    }
  }
  return out;
}

function codeOf(error: unknown): string | null {
  const code = (error as { code?: unknown } | null | undefined)?.code;
  return typeof code === 'string' ? code : null;
}

/* ------------------------------------------------------------------ *
 * Idempotency
 * ------------------------------------------------------------------ */

/**
 * A fresh uuid v4 for `Idempotency-Key` (API.md §1.8, FR-BOOK-12).
 *
 * The SAME key is reused while a confirm is being retried for the same intent, so a
 * patient who presses the button twice gets one appointment; a NEW one is minted after
 * a `409 SLOT_TAKEN`, because re-reserving an alternative is a different booking and
 * replaying the old key would answer with the conflict again.
 */
export function newIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto as Crypto | undefined;
  if (cryptoApi !== undefined && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }

  /* Older Safari and jsdom without `randomUUID`: v4 by hand, from the same CSPRNG. */
  const bytes = new Uint8Array(16);
  if (cryptoApi !== undefined && typeof cryptoApi.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let i = 0; i < bytes.length; i += 1) bytes[i] = Math.floor(Math.random() * 256);
  }
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x40;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = [...bytes].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}
